import { supabase } from '../supabaseClient';
import bcrypt from 'bcryptjs';

// Sequelize shim to keep app running without massive refactor
class Model {
  constructor(name, tableName, includeMap = {}) {
    this.name = name;
    this.tableName = tableName;
    this.includeMap = includeMap; 
  }

  _buildQuery(options, isSingle = false) {
    let selectString = '*';
    if (options.include) {
      options.include.forEach(inc => {
        const incName = inc.model.name;
        if (this.includeMap[incName]) {
          selectString += `, ${this.includeMap[incName]}`;
        }
      });
    }

    let query = supabase.from(this.tableName).select(selectString);

    if (options.where) {
      Object.keys(options.where).forEach(key => {
        const val = options.where[key];
        if (val !== undefined && val !== null && typeof val === 'object' && !Array.isArray(val)) {
          if (val.$lte !== undefined) query = query.lte(key, val.$lte);
          if (val.$gte !== undefined) query = query.gte(key, val.$gte);
        } else {
            if(val !== undefined) query = query.eq(key, val);
        }
      });
    }

    if (options.order) {
      options.order.forEach(ord => {
        const field = ord[0];
        const dir = ord[1] || 'ASC';
        query = query.order(field, { ascending: dir.toUpperCase() === 'ASC' });
      });
    }
    
    return query;
  }

  formatOutput(data, options) {
    if(!data) return data;
    const formatRow = (row) => {
      // Mock Sequelize's `row.Medio.nombre` if included
      if (options && options.include) {
         options.include.forEach(inc => {
            const incName = inc.model.name; // e.g. "Medio"
            const supabaseKey = this.includeMap[incName].split('(')[0]; // e.g. "Medios"
            if (row[supabaseKey]) {
                row[incName] = row[supabaseKey]; 
            }
         });
      }
      return row;
    };

    if (Array.isArray(data)) {
      return data.map(formatRow);
    }
    return formatRow(data);
  }

  async findAll(options = {}) {
    const { data, error } = await this._buildQuery(options);
    if (error) { console.error(`findAll error on ${this.tableName}:`, error); throw error; }
    return this.formatOutput(data, options) || [];
  }

  async findOne(options = {}) {
    // maybeSingle fetches one or zero without throwing error
    const query = this._buildQuery(options);
    const { data, error } = await query.limit(1).maybeSingle();
    if (error) { console.error(`findOne error on ${this.tableName}:`, error); throw error; }
    return this.formatOutput(data, options);
  }

  async findByPk(id) {
    const { data, error } = await supabase.from(this.tableName).select('*').eq('id', id).maybeSingle();
    if (error) { console.error(`findByPk error on ${this.tableName}:`, error); throw error; }
    return data;
  }

  async create(payload) {
    const { data, error } = await supabase.from(this.tableName).insert(payload).select().single();
    if (error) { console.error(`create error on ${this.tableName}:`, error); throw error; }
    return data;
  }

  async update(payload, options = {}) {
    let query = supabase.from(this.tableName).update(payload);
    if (options.where) {
      Object.keys(options.where).forEach(key => {
        query = query.eq(key, options.where[key]);
      });
    }
    const { data, error } = await query.select();
    if (error) { console.error(`update error on ${this.tableName}:`, error); throw error; }
    return data;
  }
  
  async destroy(options = {}) {
    let query = supabase.from(this.tableName).delete();
    if (options.where) {
      Object.keys(options.where).forEach(key => {
        query = query.eq(key, options.where[key]);
      });
    }
    const { error } = await query;
    if (error) { console.error(`destroy error on ${this.tableName}:`, error); throw error; }
    return true;
  }
}

// Instantiate "Sequelize" models targeting Supabase (no named exports — use DatabaseManager.ModelName)
const GrupoCiudad = new Model('GrupoCiudad', 'GrupoCiudads');
const Ciudad = new Model('Ciudad', 'Ciudads', { 'GrupoCiudad': 'GrupoCiudads(*)' });
const Medio = new Model('Medio', 'Medios', { 'TipoPresupuesto': 'TiposPresupuesto(*)' });
const Ejecutivo = new Model('Ejecutivo', 'Ejecutivos', { 'Ciudad': 'Ciudads(*)' });
const Parametro = new Model('Parametro', 'Parametros');
const BandaMensual = new Model('BandaMensual', 'BandaMensuals');
const BandaTrimestral = new Model('BandaTrimestral', 'BandaTrimestrals');
const Presupuesto = new Model('Presupuesto', 'Presupuestos', { 
  'Ejecutivo': 'Ejecutivos(*)', 
  'Medio': 'Medios(*)',
  'Ciudad': 'Ciudads(*)',
  'TipoPresupuesto': 'TiposPresupuesto(*)'
});
const Venta = new Model('Venta', 'Ventas', { 
  'Ejecutivo': 'Ejecutivos(*)',
  'Medio': 'Medios(*)',
  'Ciudad': 'Ciudads(*)',
  'TipoPresupuesto': 'TiposPresupuesto(*)'
});
const CalculoComision = new Model('CalculoComision', 'CalculoComisions', { 
  'Ejecutivo': 'Ejecutivos(*)',
  'Medio': 'Medios(*)',
  'Ciudad': 'Ciudads(*)' 
});

export const TipoPresupuesto = new Model('TipoPresupuesto', 'TiposPresupuesto');

Parametro.includeMap = { ...Parametro.includeMap, 'TipoPresupuesto': 'TiposPresupuesto(*)' };
BandaMensual.includeMap = { ...BandaMensual.includeMap, 'TipoPresupuesto': 'TiposPresupuesto(*)' };
BandaTrimestral.includeMap = { ...BandaTrimestral.includeMap, 'TipoPresupuesto': 'TiposPresupuesto(*)' };

export const DatabaseModels = {
  'GrupoCiudad': 'GrupoCiudads(*)',
  'Ejecutivo': 'Ejecutivos(*)',
  'Medio': 'Medios(*)',
  'Ciudad': 'Ciudads(*)',
  'TipoPresupuesto': 'TiposPresupuesto(*)'
};

async function loadInitialData() {
  // Check if admin exists
  const admin = await Ejecutivo.findOne({ where: { email: 'admin@rcn.com.co' } });
  if (!admin) {
    const hashedPassword = await bcrypt.hash('admin123', 10);
    
    // Create base City Groups
    const groupBogota = await GrupoCiudad.create({ nombre: 'Bogotá', descripcion: 'Sede Principal Bogotá' });
    const groupRegionales = await GrupoCiudad.create({ nombre: 'Regionales', descripcion: 'Sedes Regionales' });
    
    const newCity = await Ciudad.create({ nombre: 'Bogotá D.C.', grupoCiudadId: groupBogota.id });
    await Ciudad.create({ nombre: 'Medellín', grupoCiudadId: groupRegionales.id });
    await Ciudad.create({ nombre: 'Cali', grupoCiudadId: groupRegionales.id });
    await Ciudad.create({ nombre: 'Barranquilla', grupoCiudadId: groupRegionales.id });
    
    // Medios
    await Medio.create({ nombre: 'Radio - Canal', mixAnual: 0.6, pesoEstrategico: 1.0 });
    await Medio.create({ nombre: 'Digital', mixAnual: 0.2, pesoEstrategico: 1.1 });
    await Medio.create({ nombre: 'LR/Eventos', mixAnual: 0.2, pesoEstrategico: 1.2 });



    await Ejecutivo.create({
      nombre: 'Administrador Sistema',
      email: 'admin@rcn.com.co',
      password: hashedPassword,
      role: 'admin',
      ciudadId: newCity.id,
      perfilVenta: 'N/A'
    });

  }

  // Ensure parameters and bands exist even if admin was already there
  const paramCount = await Parametro.findAll();
  if (paramCount.length === 0) {
    const params = [
      { nombre: 'tasa_base_comision', valor: '0.007', descripcion: 'Tasa base total sobre ventas (0.7%)' },
      { nombre: 'bolsa_mensual', valor: '0.34', descripcion: 'Porcentaje de la comisión que se paga mensual' },
      { nombre: 'bolsa_trimestral', valor: '0.66', descripcion: 'Porcentaje de la comisión que se paga trimestral' },
      { nombre: 'umbral_minimo_medio', valor: '0.7', descripcion: 'Cumplimiento mínimo por medio para comisionar (Bloqueo)' },
      { nombre: 'compuerta_integral_mensual', valor: '0.85', descripcion: 'Cumplimiento integral mínimo mensual' },
      { nombre: 'compuerta_integral_trimestral', valor: '0.9', descripcion: 'Cumplimiento integral mínimo trimestral' },
      { nombre: 'medios_bajo_70', valor: '1', descripcion: 'Cantidad de medios bajo 70% que bloquean pago' },
      { nombre: 'balance_condicion_min_0.7', valor: '0.0', descripcion: 'Factor balance si algún medio < 70%' },
      { nombre: 'balance_condicion_min_0.8', valor: '0.7', descripcion: 'Factor balance si algún medio < 80%' },
      { nombre: 'balance_condicion_todos_0.85', valor: '1.0', descripcion: 'Factor balance si todos >= 85%' },
      { nombre: 'balance_condicion_todos_0.95', valor: '1.1', descripcion: 'Factor balance si todos >= 95%' },
      { nombre: 'balance_condicion_todos_1.00', valor: '1.25', descripcion: 'Factor balance si todos >= 100%' }
    ];
    for (const p of params) { await Parametro.create(p); }
  }

  const bandCount = await BandaMensual.findAll();
  if (bandCount.length === 0) {
    const bands = [
      { nombre: 'Sin Pago', desde: 0, hasta: 0.6999, factorIndividual: 0, factorIntegral: 0 },
      { nombre: 'Banda 1', desde: 0.7, hasta: 0.8499, factorIndividual: 0.5, factorIntegral: 0 },
      { nombre: 'Banda 2', desde: 0.85, hasta: 0.9499, factorIndividual: 0.8, factorIntegral: 0.8 },
      { nombre: 'Banda 3', desde: 0.95, hasta: 0.9999, factorIndividual: 1.0, factorIntegral: 1.0 },
      { nombre: 'Banda 4', desde: 1.0, hasta: 1.0999, factorIndividual: 1.15, factorIntegral: 1.1 },
      { nombre: 'Banda 5', desde: 1.1, hasta: 9.99, factorIndividual: 1.3, factorIntegral: 1.2 }
    ];
    for (const b of bands) {
      await BandaMensual.create({ ...b, activo: true });
      await BandaTrimestral.create({ ...b, activo: true });
    }
  }

  // Tipos de Presupuesto - Ensure Base and Ppto Adicional
  const existingTypes = await TipoPresupuesto.findAll();
  if (existingTypes.length === 0) {
    await TipoPresupuesto.create({ nombre: 'Base', descripcion: 'Presupuesto principal del ejecutivo' });
    await TipoPresupuesto.create({ nombre: 'Ppto Adicional', descripcion: 'Presupuestos adicionales o extraordinarios' });
  }

  // Auto-migration for legacy "tipo" string column
  const allParams = await Parametro.findAll();
  const allBandsM = await BandaMensual.findAll();
  const allBandsT = await BandaTrimestral.findAll();
  const allMedios = await Medio.findAll();
  
  const modelsToMigrate = [
    { model: Parametro, data: allParams },
    { model: BandaMensual, data: allBandsM },
    { model: BandaTrimestral, data: allBandsT },
    { model: Medio, data: allMedios }
  ];

  for (const entry of modelsToMigrate) {
    for (const record of entry.data) {
      if (record.tipo && !record.tipoPresupuestoId) {
        let typeObj = (await TipoPresupuesto.findAll()).find(t => t.nombre.toLowerCase() === record.tipo.toLowerCase());
        if (!typeObj) {
          typeObj = await TipoPresupuesto.create({ nombre: record.tipo, descripcion: 'Migrado de sistema anterior' });
        }
        await entry.model.update({ tipoPresupuestoId: typeObj.id }, { where: { id: record.id } });
      }
    }
  }

  return true;
}

const DatabaseManager = {
  GrupoCiudad,
  Ciudad,
  Ejecutivo,
  Medio,
  Parametro,
  BandaMensual,
  BandaTrimestral,
  Presupuesto,
  Venta,
  CalculoComision,
  TipoPresupuesto,
  loadInitialData,
  initialize: loadInitialData  // alias used by App.js
};

export default DatabaseManager;